import { NgFor, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DosageOption } from '@models/dosage-upgrade/dosage-option';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { firstValueFrom } from 'rxjs';

import { UpgradeDosageUpsellComponent } from '../upgrade-dosage-upsell.component';

/**
 * STD Prevention specific dosage upgrade upsell component.
 *
 * Loads STD Prevention dosage configuration from JSON and passes it to the
 * UpgradeDosageUpsellComponent for rendering.
 */
@Component({
  selector: 'app-std-prevention-upgrade-dosage-upsell',
  standalone: true,
  imports: [NgIf, NgFor, ReactiveFormsModule, UpgradeDosageUpsellComponent],
  templateUrl: './std-prevention-upgrade-dosage-upsell.component.html',
})
export class StdPreventionUpgradeDosageUpsellComponent implements OnInit {
  @Input() public testId!: number;
  @ViewChild(UpgradeDosageUpsellComponent, { static: false })
  public upgradeDosageUpsellComponent: UpgradeDosageUpsellComponent;

  public upgradeDosageOptions: DosageOption[] = [];
  public currentDosageData: any;
  public form: FormGroup;

  private http = inject(HttpClient);
  private consultationStorageService = inject(ConsultationStorageService);

  /**
   * Initialize component and load dosage configuration
   */
  public async ngOnInit() {
    try {
      await this.loadDosageConfig();
    } catch (error) {
      this.upgradeDosageOptions = [];
      this.form = null;
    }
  }

  /**
   * Load and build STD Prevention dosage configuration from JSON file
   */
  private async loadDosageConfig(): Promise<void> {
    const dosages: any[] = await firstValueFrom(
      this.http.get<any[]>('assets/questionnaires/std-prevention-dosages.json'),
    );

    this.currentDosageData = this.findCurrentDosage(dosages);
    this.upgradeDosageOptions = this.buildUpgradeOptions(dosages, this.currentDosageData.upgradeOptions);

    if (this.upgradeDosageOptions.length === 0) {
      this.form = null;
    } else {
      this.form = new FormGroup({
        upgradeDosage: new FormControl('', Validators.required),
      });
    }
  }

  /**
   * Find current dosage data by test ID
   *
   * @param dosages Array of dosage data from JSON
   */
  private findCurrentDosage(dosages: any[]): any {
    const currentDosageData = dosages.find((d: any) => d.id === this.testId);

    if (!currentDosageData) {
      throw new Error('Current dosage not found');
    }

    return currentDosageData;
  }

  /**
   * Build upgrade options from upgrade IDs
   *
   * @param dosages Array of all dosage data
   * @param upgradeIds Array of upgrade dosage IDs
   */
  private buildUpgradeOptions(dosages: any[], upgradeIds: number[]): DosageOption[] {
    const upgradeOptions = upgradeIds || [];
    const treatmentTests = this.consultationStorageService.treatmentPreferenceTests;

    return upgradeOptions
      .map((upgradeId: number) => {
        const upgrade = dosages.find((d: any) => d.id === upgradeId);
        if (!upgrade) {
          throw new Error(`Upgrade dosage with id ${upgradeId} not found`);
        }

        const treatmentTest = treatmentTests.find((t) => t.id === upgradeId);
        if (!treatmentTest) {
          throw new Error(`Treatment test with id ${upgradeId} not found in eligibleTreatmentTypes`);
        }

        return {
          value: treatmentTest.name,
          label: upgrade.dosage,
          price: upgrade.price,
        };
      })
      .sort((a: DosageOption, b: DosageOption) => parseInt(b.label) - parseInt(a.label));
  }
}
